import React, { useState, useContext, useEffect, useLayoutEffect } from "react";
import PropTypes from "prop-types";
import { isNumber } from "lodash";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import { useOnResize } from "helpers/hooks";
import { PageLoading } from "helpers/loading";
import withSuspense from "helpers/withSuspense";
import Sidebar from "../sidebar/Sidebar";
import NavBar from "../navbar/NavBar";
import LoadingScreen from "../loadingscreen/LoadingScreen";
import FlashMessage from "components/flashMessage/flashMessage";
import ErrorBoundary from "components/error/ErrorBoundary";
import { FooterContext } from "components/footer/FooterContext";
import { toggleButtonOnClick } from "actions";
import { useAuth } from "./useAuth";
import { SidebarCollapsedSetting } from "helpers/usersettings/usersettings";
import "./Layout.scss";

const Routes = withSuspense(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "routes" */
        /* webpackPrefetch: true */
        "pages/Routes"
      )
  ),
  PageLoading
);

function useIsMobile() {
  const [isMobile, setMobile] = useState(
    window.matchMedia("(max-width: 768px)").matches
  );
  const resizeHandler = () => {
    window.matchMedia("(max-width: 768px)").matches
      ? setMobile(true)
      : setMobile(false);
  };

  useOnResize(resizeHandler);

  return isMobile;
}

const Layout = ({ isLoading, ...props }) => {
  const history = useHistory();
  const [isCollapsed, setIsCollapsed] = useState(() => {
    return SidebarCollapsedSetting.get() ?? false;
  });
  const isMobile = useIsMobile();
  const { hasFooter } = useContext(FooterContext);
  const isAuthenticated = useAuth();

  useEffect(
    () => () => {
      document.title = "Texada";
    },
    []
  );

  useEffect(() => {
    if (SidebarCollapsedSetting.get() !== isCollapsed)
      SidebarCollapsedSetting.save(isCollapsed);
  }, [isCollapsed]);

  useLayoutEffect(() => {
    const { classList } = document.querySelector("#container");
    if (isCollapsed) {
      classList.add("collapsed");
    } else {
      classList.remove("collapsed");
    }

    return function preventContainerAnimationOnLoad() {
      document.querySelector("#container").classList.add("loaded");
    };
  }, [isCollapsed]);

  if (!isAuthenticated) return null;

  return (
    <>
      <LoadingScreen
        refresh={props.refresh}
        isLoading={
          isLoading === true ||
          (isNumber(isLoading) ? isLoading > 0 : isLoading)
        }
      />
      <Sidebar
        toggleSideBar={props.clickToggleButton}
        toggleButtonOnClick={props.toggleButtonOnClick}
        isMobile={isMobile}
        isCollapsed={isCollapsed}
        setIsCollapsed={setIsCollapsed}
      />
      <NavBar toggleButtonOnClick={props.toggleButtonOnClick} />
      <div className={`page-content ${hasFooter ? "with-footer" : ""}`}>
        <ErrorBoundary>
          <FlashMessage url={history.location.pathname} />
          <Routes perms={props.user} />
        </ErrorBoundary>
      </div>
    </>
  );
};

Layout.propTypes = {
  clickToggleButton: PropTypes.bool,
  toggleButtonOnClick: PropTypes.func,
  children: PropTypes.node
};

const mapStateToProps = state => {
  return {
    clickToggleButton: state.clickToggleButton,
    isLoading: state.isLoading,
    refresh: state.refresh,
    user: state.auth.user
  };
};

const mapDispatchToProps = {
  toggleButtonOnClick
};

const LayoutComponent: React.FC = connect(
  mapStateToProps,
  mapDispatchToProps
)(SidebarCollapsedSetting.withProvider(Layout as any));

LayoutComponent.displayName = "Layout";

export default LayoutComponent;
